/**
 * Notification Service
 * Handles all notification-related API calls
 */

import axios from '../api/axios';
import { API_ENDPOINTS } from './api.config';

class NotificationService {
  /**
   * Get user notifications
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @returns {Promise<Object>} Response with notifications array and pagination
   */
  async getNotifications(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 20,
      });

      const response = await axios.get(
        `${API_ENDPOINTS.NOTIFICATIONS.LIST}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Send notification (admin only)
   * @param {Object} notificationData - Notification data
   * @param {string} notificationData.user_id - Target user ID (optional)
   * @param {string} notificationData.notification_type - Type of notification
   * @param {string} notificationData.title - Notification title
   * @param {string} notificationData.message - Notification message
   * @param {Object} notificationData.data - Additional data (optional)
   * @returns {Promise<Object>} Response with created notification
   */
  async sendNotification(notificationData) {
    try {
      const response = await axios.post(
        API_ENDPOINTS.NOTIFICATIONS.SEND,
        notificationData
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Mark notification as read
   * @param {number} notificationId - Notification ID
   * @returns {Promise<Object>} Response with success status
   */
  async markAsRead(notificationId) {
    try {
      const response = await axios.put(
        API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notificationId)
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Mark all notifications as read
   * @returns {Promise<Object>} Response with success status and count
   */
  async markAllAsRead() {
    try {
      const response = await axios.put(
        API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get unread notifications count
   * @returns {Promise<Object>} Response with count
   */
  async getUnreadCount() {
    try {
      const response = await axios.get(
        API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get user notification preferences
   * @returns {Promise<Object>} Response with preferences object
   */
  async getPreferences() {
    try {
      const response = await axios.get(
        API_ENDPOINTS.NOTIFICATIONS.PREFERENCES
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update user notification preferences
   * @param {Object} preferences - Preferences to update
   * @param {boolean} preferences.email_notifications - Email notifications enabled
   * @param {boolean} preferences.sms_notifications - SMS notifications enabled
   * @param {boolean} preferences.push_notifications - Push notifications enabled
   * @param {boolean} preferences.appointment_reminders - Appointment reminder notifications
   * @param {boolean} preferences.order_updates - Order update notifications
   * @param {boolean} preferences.promotional - Promotional notifications
   * @returns {Promise<Object>} Response with updated preferences
   */
  async updatePreferences(preferences) {
    try {
      const response = await axios.put(
        API_ENDPOINTS.NOTIFICATIONS.UPDATE_PREFERENCES,
        preferences
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get notification status/summary
   * @returns {Promise<Object>} Response with notification status
   */
  async getStatus() {
    try {
      const response = await axios.get(API_ENDPOINTS.NOTIFICATIONS.STATUS);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      const errorMessage =
        error.response.data.error ||
        error.response.data.message ||
        'Something went wrong with notifications';
      return new Error(errorMessage);
    } else if (error.request) {
      return new Error('Network error. Please check your connection.');
    } else {
      return new Error('An error occurred. Please try again.');
    }
  }
}

export default new NotificationService();
